import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";

interface LinkedInWizardStep9Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const VOICE_STYLES = [
  {
    value: "corporate-polished",
    label: "Corporate / Polished",
    description: "Professional, formal, and executive-level tone"
  },
  {
    value: "human-clear",
    label: "Human and Clear", 
    description: "Conversational, approachable, and easy to understand"
  },
  {
    value: "bold-edgy",
    label: "Bold and Edgy",
    description: "Confident, disruptive, and attention-grabbing"
  },
  {
    value: "inspirational",
    label: "Inspirational or Purpose-driven",
    description: "Motivational, values-focused, and mission-oriented"
  },
  {
    value: "warm-faith",
    label: "Warm and Faith-forward",
    description: "Personal, values-based, with spiritual elements"
  },
  {
    value: "values-action",
    label: "Values-led with Strong Call to Action",
    description: "Purpose-driven with clear next steps"
  }
];

const FAITH_OPTIONS = [
  {
    value: "yes",
    label: "Yes - Include faith/values prominently",
    description: "Make faith and values a central part of your profile"
  },
  {
    value: "light",
    label: "Light mention only",
    description: "Subtle reference to values and deeper purpose"
  },
  {
    value: "no",
    label: "No - Keep it secular",
    description: "Focus on professional accomplishments only"
  }
];

export function LinkedInWizardStep9({ data, updateData }: LinkedInWizardStep9Props) {
  // Determine most popular voice style based on industry
  const getMostPopularVoiceStyle = () => {
    const industries = data.industries || [];
    const industryString = industries.join(' ').toLowerCase();
    
    // Corporate/Finance/Banking/Tech industries
    if (industryString.includes('finance') || industryString.includes('banking') || 
        industryString.includes('corporate') || industryString.includes('technology') ||
        industryString.includes('software') || industryString.includes('consulting')) {
      return 'corporate-polished';
    }
    
    // Creative/Marketing/Design industries
    if (industryString.includes('marketing') || industryString.includes('advertising') ||
        industryString.includes('design') || industryString.includes('media') ||
        industryString.includes('creative') || industryString.includes('arts')) {
      return 'human-clear';
    }
    
    // Nonprofit/Healthcare/Education
    if (industryString.includes('nonprofit') || industryString.includes('healthcare') ||
        industryString.includes('education') || industryString.includes('social')) {
      return 'inspirational';
    }
    
    // Startup/Entrepreneurship/Innovation
    if (industryString.includes('startup') || industryString.includes('entrepreneur') ||
        industryString.includes('innovation') || industryString.includes('venture')) {
      return 'bold-edgy';
    }
    
    // Default - most versatile
    return 'human-clear';
  };

  const getMostPopularFaithOption = () => {
    const industries = data.industries || [];
    const industryString = industries.join(' ').toLowerCase();
    
    // Industries where light mention is often popular
    if (industryString.includes('nonprofit') || industryString.includes('education') ||
        industryString.includes('healthcare') || industryString.includes('social')) {
      return 'light';
    }
    
    // Default - most common choice
    return 'no';
  };

  const mostPopularVoice = getMostPopularVoiceStyle();
  const mostPopularFaith = getMostPopularFaithOption();
  return (
    <div className="space-y-8">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">9</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">Voice, Style & Values</h2>
          <p className="text-linkedin/70 mb-6">
            Define how you want your LinkedIn profile to sound and whether you want to include deeper mission or values.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-lg font-semibold mb-4 block text-linkedin">
            How do you want your profile to sound? *
          </Label>
          <RadioGroup
            value={data.voiceStyle || ""}
            onValueChange={(value) => updateData({ voiceStyle: value })}
            className="space-y-3"
          >
            {VOICE_STYLES.map((style) => {
              const isSelected = data.voiceStyle === style.value;
              const isMostPopular = style.value === mostPopularVoice;
              
              return (
                  <Card 
                    key={style.value} 
                    className={`p-4 relative transition-all border-linkedin/20 ${
                      isSelected 
                        ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200 shadow-md' 
                        : 'border-linkedin/30 bg-linkedin-light/10 hover:border-linkedin/50 hover:bg-linkedin-light/20'
                    }`}
                >
                  {isMostPopular && (
                    <Badge className="absolute -top-2 -right-2 bg-linkedin text-white px-3 py-1 text-xs font-bold border-2 border-white shadow-lg">
                      <Star className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  )}
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem 
                      value={style.value} 
                      id={style.value}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={style.value}
                        className={`font-medium cursor-pointer block ${
                          isSelected ? 'text-green-800 dark:text-green-200' : 'text-linkedin dark:text-linkedin'
                        }`}
                      >
                        {style.label}
                      </label>
                      <p className={`text-sm mt-1 ${
                        isSelected ? 'text-green-700 dark:text-green-300' : 'text-linkedin/70 dark:text-linkedin/70'
                      }`}>
                        {style.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </RadioGroup>
        </div>

        <div>
          <Label className="text-lg font-semibold mb-4 block text-linkedin">
            Do you want to mention faith, deeper mission, or values? *
          </Label>
          <RadioGroup
            value={data.includeFaith || ""}
            onValueChange={(value) => updateData({ includeFaith: value })}
            className="space-y-3"
          >
            {FAITH_OPTIONS.map((option) => {
              const isSelected = data.includeFaith === option.value;
              const isMostPopular = option.value === mostPopularFaith;
              
              return (
                  <Card 
                    key={option.value} 
                    className={`p-4 relative transition-all border-linkedin/20 ${
                      isSelected 
                        ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200 shadow-md' 
                        : 'border-linkedin/30 bg-linkedin-light/10 hover:border-linkedin/50 hover:bg-linkedin-light/20'
                    }`}
                >
                  {isMostPopular && (
                    <Badge className="absolute -top-2 -right-2 bg-linkedin text-white px-3 py-1 text-xs font-bold border-2 border-white shadow-lg">
                      <Star className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  )}
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem 
                      value={option.value} 
                      id={option.value}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={option.value}
                        className={`font-medium cursor-pointer block ${
                          isSelected ? 'text-green-800 dark:text-green-200' : 'text-linkedin dark:text-linkedin'
                        }`}
                      >
                        {option.label}
                      </label>
                      <p className={`text-sm mt-1 ${
                        isSelected ? 'text-green-700 dark:text-green-300' : 'text-linkedin/70 dark:text-linkedin/70'
                      }`}>
                        {option.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </RadioGroup>
        </div>
      </div>

      {data.voiceStyle && data.includeFaith && (
        <Card className="p-4 bg-linkedin-light border-linkedin/20">
          <h3 className="font-semibold text-linkedin mb-2">
            Your Profile Style:
          </h3>
          <div className="text-linkedin/80 space-y-1">
            <p><strong>Voice:</strong> {VOICE_STYLES.find(s => s.value === data.voiceStyle)?.label}</p>
            <p><strong>Values:</strong> {FAITH_OPTIONS.find(o => o.value === data.includeFaith)?.label}</p>
          </div>
        </Card>
      )}

      <Card className="p-4 bg-linkedin-light border-linkedin/20">
        <h3 className="font-semibold text-linkedin mb-3">
          Style Examples:
        </h3>
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium text-linkedin/80">Corporate/Polished:</span>
            <span className="text-linkedin/70 ml-2">
              "Results-driven executive with 15+ years driving organizational transformation..."
            </span>
          </div>
          <div>
            <span className="font-medium text-linkedin/80">Human/Clear:</span>
            <span className="text-linkedin/70 ml-2">
              "I help busy entrepreneurs simplify their operations so they can focus on what matters most..."
            </span>
          </div>
          <div>
            <span className="font-medium text-linkedin/80">Bold/Edgy:</span>
            <span className="text-linkedin/70 ml-2">
              "I break the rules of traditional marketing to get unconventional results..."
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}